import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import Modal from '../../controls/Modal';
import LabeledInput from '../../controls/LabeledInput';
import LabeledSelect from '../../controls/LabeledSelect';
import SpiteToggle from './SpiteToggle';
import ImageUpload from './ImageUpload';

const LIMIT_OPTIONS = [
    { key: 'UNLIMITED', value: 'Без ограничений' },
    { key: 'LIMIT', value: 'Лимит на количество' }
];

// Модалка создания/редактирования задания.
export default function TaskModal({ task, neighborId, smenaId, categories, defaultCategoryId, onSuccess, onClose }) {
    const editing = !!task;
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({
        categoryId: task?.categoryId || defaultCategoryId || '',
        subcategoryId: task?.subcategoryId || '',
        title: task?.title || '',
        description: task?.description || '',
        conditions: task?.conditions || '',
        cost: task?.cost ?? 10,
        withTargetUser: task?.withTargetUser || false,
        requiresBothConfirmation: task?.requiresBothConfirmation || false,
        isChallenge: task?.isChallenge || false,
        isActive: editing ? task.isActive !== false : true,
        useTime: task?.useTime || false,
        duration: task?.duration || '',
        involvedInDaily: task?.involvedInDaily || false,
        availableCountType: task?.taskAvailableCountType || 'UNLIMITED',
        availableCount: task?.availableCount ?? null
    });

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
    const toggle = (field) => () => set(field, !form[field]);

    const { data: subcategories = [] } = useApiQuery(
        ['neighbor', 'task-subcategories', form.categoryId],
        `/api/neighbor/admin/task-subcategories/list-by-category?categoryId=${form.categoryId}`,
        { enabled: !!form.categoryId }
    );

    const submit = async () => {
        if (!form.categoryId || !form.title.trim()) {
            enqueueSnackbar('Заполните обязательные поля', { variant: 'warning' });
            return;
        }
        if (!editing && !form.subcategoryId) {
            enqueueSnackbar('Выберите подкатегорию (если список пуст — создайте её в панели категорий)', { variant: 'warning' });
            return;
        }
        const { availableCountType, ...rest } = form;
        const requestData = {
            ...(editing ? {} : { neighborId, smenaId }),
            ...rest,
            subcategoryId: form.subcategoryId || null,
            taskAvailableCountType: availableCountType,
            availableCount: availableCountType !== 'UNLIMITED' ? form.availableCount : null,
            duration: form.useTime ? form.duration : null
        };
        const fd = new FormData();
        fd.append('request', new Blob([JSON.stringify(requestData)], { type: 'application/json' }));
        if (file) fd.append('file', file);
        show();
        try {
            if (editing) {
                await apiClient.patch(`/api/neighbor/admin/tasks/update?id=${task.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await apiClient.post('/api/neighbor/admin/tasks/create', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            enqueueSnackbar(editing ? 'Задание обновлено' : 'Задание создано', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    const del = async () => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/tasks/delete?id=${task.id}`);
            enqueueSnackbar('Задание удалено', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (e) { enqueueSnackbar('Ошибка при удалении', { variant: 'error' }); }
        finally { hide(); }
    };

    const catOptions = categories.map(c => ({ key: c.id, value: c.displayName }));
    const subOptions = subcategories.map(s => ({ key: s.id, value: s.displayName }));

    const footer = (
        <>
            {editing && <button type="button" className="btn btn-danger" style={{ marginRight: 'auto' }} onClick={del}>Удалить</button>}
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="button" className="btn btn-primary" onClick={submit}>{editing ? 'Сохранить' : 'Создать'}</button>
        </>
    );

    return (
        <Modal open kicker="Задание" title={editing ? 'Редактирование задания' : 'Создать задание'} size="lg" onClose={onClose} footer={footer}>
            <div className="fr-grid2">
                <LabeledSelect label="Категория" value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)} options={catOptions} placeholder="Выберите категорию" />
                <LabeledSelect label="Подкатегория" value={form.subcategoryId} onChange={(e) => set('subcategoryId', e.target.value)} options={subOptions} placeholder="Выберите подкатегорию" />
            </div>
            <LabeledInput label="Название" value={form.title} onChange={(e) => set('title', e.target.value)} />
            <div>
                <label className="ctl-label">Описание</label>
                <textarea className="textarea" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
                <label className="ctl-label">Условия выполнения</label>
                <textarea className="textarea" value={form.conditions} onChange={(e) => set('conditions', e.target.value)} />
            </div>
            <div className="fr-grid2">
                <LabeledInput label="Стоимость (SOL)" type="number" value={String(form.cost)} onChange={(e) => set('cost', parseInt(e.target.value, 10) || 0)} />
                <LabeledSelect label="Тип ограничения" value={form.availableCountType} onChange={(e) => set('availableCountType', e.target.value)} options={LIMIT_OPTIONS} />
            </div>
            {form.availableCountType !== 'UNLIMITED' && (
                <LabeledInput label="Доступное количество" type="number" value={form.availableCount == null ? '' : String(form.availableCount)} onChange={(e) => set('availableCount', e.target.value === '' ? null : parseInt(e.target.value, 10))} />
            )}

            <div className="sp-sec">
                <h4>Настройки</h4>
                <div className="sp-tgs">
                    <SpiteToggle label="Активно" checked={form.isActive} onChange={toggle('isActive')} />
                    <SpiteToggle label="Назначаемое (с целью)" checked={form.withTargetUser} onChange={toggle('withTargetUser')} />
                    <SpiteToggle label="Подтверждение обоих" checked={form.requiresBothConfirmation} onChange={toggle('requiresBothConfirmation')} />
                    <SpiteToggle label="Участвует в рулетке" checked={form.involvedInDaily} onChange={toggle('involvedInDaily')} />
                    <SpiteToggle label="Ограничение по времени" checked={form.useTime} onChange={toggle('useTime')} full />
                    {form.useTime && (
                        <div className="sp-tg-full">
                            <LabeledInput label="Длительность (ЧЧ:ММ:СС)" value={form.duration} onChange={(e) => set('duration', e.target.value)} placeholder="00:30:00" />
                        </div>
                    )}
                </div>
            </div>

            <div className="sp-sec">
                <h4>Изображение</h4>
                <ImageUpload existingUrl={task?.mediaFile?.mediaFileUrl || null} pendingFile={file} onFileSelect={setFile} onClear={() => setFile(null)} />
            </div>
        </Modal>
    );
}

TaskModal.propTypes = {
    task: PropTypes.object,
    neighborId: PropTypes.number,
    smenaId: PropTypes.number,
    categories: PropTypes.array.isRequired,
    defaultCategoryId: PropTypes.number,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

TaskModal.defaultProps = { task: null, neighborId: null, smenaId: null, defaultCategoryId: null };
